/// <reference types="web-bluetooth" />
// useBluetooth — Web Bluetooth API hook for cycling sensors.
//
// Supports two BLE GATT services:
//   - Cycling Power (0x1818) — power in watts, cadence in RPM
//   - Heart Rate (0x180D) — heart rate in bpm
//
// These are the standard profiles used by Wahoo KICKR, Garmin, Stages,
// 4iiii, Favero, etc. The hook exposes a connect function that triggers
// the browser's native BLE device picker — the user selects their device,
// we subscribe to notifications, and live readings flow into state.
//
// Usage:
//   const ble = useBluetooth();
//   <button onClick={ble.connectPower}>Connect power meter</button>
//   <span>{ble.power?.watts} W</span>

import { useCallback, useRef, useState } from 'react';

// BLE GATT UUIDs
const CYCLING_POWER_SERVICE = 0x1818;
const CYCLING_POWER_MEASUREMENT = 0x2a63;
const HEART_RATE_SERVICE = 0x180d;
const HEART_RATE_MEASUREMENT = 0x2a37;

// Wave 20.44 — FTMS (Fitness Machine Service) for controllable
// smart trainers. Standard BLE profile — Wahoo Kickr, Tacx Neo,
// Elite Suito, Saris H4, Zwift Hub, etc. all speak it. Many of
// these ALSO expose Cycling Power (0x1818) so a single paired
// device covers both read (power/cadence) and write (ERG target).
const FTMS_SERVICE = 0x1826;
const FTMS_CONTROL_POINT = 0x2ad9;
// Fitness Machine Control Point op codes we use:
//   0x00 Request Control
//   0x01 Reset
//   0x05 Set Target Power (int16, watts, little-endian)
const FTMS_OP_REQUEST_CONTROL = 0x00;
const FTMS_OP_SET_TARGET_POWER = 0x05;

// LocalStorage key so Preflight → AIClientVision can hand the
// last-set target across the pairing gap (device has to be
// re-paired between screens; the number persists).
const TARGET_POWER_LS_KEY = 'aerochat.targetPowerW';

export interface PowerReading {
  watts: number;
  cadenceRpm: number | null;
  timestamp: number;
}

export interface HeartRateReading {
  bpm: number;
  timestamp: number;
}

export interface BluetoothState {
  // Power meter
  powerDevice: string | null;
  powerConnected: boolean;
  power: PowerReading | null;
  connectPower: () => Promise<void>;
  disconnectPower: () => void;

  // Heart rate
  hrDevice: string | null;
  hrConnected: boolean;
  heartRate: HeartRateReading | null;
  connectHr: () => Promise<void>;
  disconnectHr: () => void;

  // Wave 20.44 — Smart-trainer ERG control via FTMS. Populated
  // opportunistically during connectPower: if the paired device
  // also advertises 0x1826, we take control and hasErgControl
  // flips true. Otherwise setTargetPower is a no-op.
  hasErgControl: boolean;
  targetPowerW: number | null;
  setTargetPower: (watts: number) => Promise<void>;

  // Combined sensor data for the vision pipeline
  getSensorData: () => {
    power_w?: number;
    cadence_rpm?: number;
    heart_rate_bpm?: number;
  } | undefined;

  error: string | null;
}

export function useBluetooth(): BluetoothState {
  const [powerDevice, setPowerDevice] = useState<string | null>(null);
  const [powerConnected, setPowerConnected] = useState(false);
  const [power, setPower] = useState<PowerReading | null>(null);

  const [hrDevice, setHrDevice] = useState<string | null>(null);
  const [hrConnected, setHrConnected] = useState(false);
  const [heartRate, setHeartRate] = useState<HeartRateReading | null>(null);

  const [error, setError] = useState<string | null>(null);

  const powerGattRef = useRef<BluetoothRemoteGATTServer | null>(null);
  const hrGattRef = useRef<BluetoothRemoteGATTServer | null>(null);

  // Keep latest readings in refs so getSensorData doesn't trigger re-renders
  const powerRef = useRef<PowerReading | null>(null);
  const hrRef = useRef<HeartRateReading | null>(null);

  // Wave 20.44 — FTMS control point handle. Present when the
  // paired power meter is actually a smart trainer speaking FTMS.
  const ftmsControlPointRef = useRef<BluetoothRemoteGATTCharacteristic | null>(null);
  const [hasErgControl, setHasErgControl] = useState(false);
  const [targetPowerW, setTargetPowerWState] = useState<number | null>(() => {
    try {
      const raw = localStorage.getItem(TARGET_POWER_LS_KEY);
      const n = raw ? Number(raw) : NaN;
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  });

  // Cumulative crank revolutions for cadence calculation
  const lastCrankRevs = useRef<number | null>(null);
  const lastCrankTime = useRef<number | null>(null);

  // ---- Power meter ----

  const connectPower = useCallback(async () => {
    if (!navigator.bluetooth) {
      setError('Web Bluetooth is not supported in this browser. Use Chrome.');
      return;
    }
    setError(null);
    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [CYCLING_POWER_SERVICE] }],
        // Wave 20.44 — advertise interest in FTMS as an optional
        // service so Web Bluetooth exposes it after pairing without
        // requiring users to re-pick their trainer.
        optionalServices: [FTMS_SERVICE],
      });
      setPowerDevice(device.name ?? 'Power meter');

      device.addEventListener('gattserverdisconnected', () => {
        setPowerConnected(false);
        setPowerDevice(null);
        powerGattRef.current = null;
        ftmsControlPointRef.current = null;
        setHasErgControl(false);
      });

      const server = await device.gatt!.connect();
      powerGattRef.current = server;
      setPowerConnected(true);

      const service = await server.getPrimaryService(CYCLING_POWER_SERVICE);
      const char = await service.getCharacteristic(CYCLING_POWER_MEASUREMENT);

      char.addEventListener(
        'characteristicvaluechanged',
        (event: Event) => {
          const value = (event.target as BluetoothRemoteGATTCharacteristic)
            .value!;
          const reading = parseCyclingPower(value);
          powerRef.current = reading;
          setPower(reading);
        }
      );

      await char.startNotifications();

      // Wave 20.44 — Opportunistically wire FTMS ERG control if the
      // paired device speaks it. Silent on failure — a plain power
      // meter without FTMS just gets read-only mode.
      try {
        const ftms = await server.getPrimaryService(FTMS_SERVICE);
        const cp = await ftms.getCharacteristic(FTMS_CONTROL_POINT);
        ftmsControlPointRef.current = cp;
        // Request Control (0x00) — every subsequent op needs this
        // permission granted first.
        await cp.writeValueWithResponse(new Uint8Array([FTMS_OP_REQUEST_CONTROL]));
        setHasErgControl(true);
        // If a target power was set in a prior session, push it
        // through immediately so the trainer picks up where we
        // left off.
        const stored = localStorage.getItem(TARGET_POWER_LS_KEY);
        const w = stored ? Number(stored) : NaN;
        if (Number.isFinite(w) && w > 0) {
          await writeSetTargetPower(cp, Math.round(w));
        }
      } catch {
        setHasErgControl(false);
        ftmsControlPointRef.current = null;
      }
    } catch (e: any) {
      if (e.name === 'NotFoundError') {
        // User cancelled the device picker — not an error
        return;
      }
      setError(e.message || 'Failed to connect power meter');
    }
  }, []);

  const disconnectPower = useCallback(() => {
    if (powerGattRef.current?.connected) {
      powerGattRef.current.disconnect();
    }
    powerGattRef.current = null;
    setPowerConnected(false);
    setPowerDevice(null);
    setPower(null);
    powerRef.current = null;
    lastCrankRevs.current = null;
    lastCrankTime.current = null;
    ftmsControlPointRef.current = null;
    setHasErgControl(false);
  }, []);

  // Wave 20.44 — Send a target-power ERG command to the trainer.
  // Persists the value to localStorage regardless of whether the
  // trainer is currently connected so Preflight → AIClientVision
  // (each with their own useBluetooth pairing) both see the same
  // number.
  const setTargetPower = useCallback(async (watts: number) => {
    const clamped = Math.max(25, Math.min(2000, Math.round(watts)));
    try {
      localStorage.setItem(TARGET_POWER_LS_KEY, String(clamped));
    } catch { /* storage disabled — target lives in state only */ }
    setTargetPowerWState(clamped);
    const cp = ftmsControlPointRef.current;
    if (!cp) return; // no trainer or FTMS not wired — value is remembered for later
    try {
      await writeSetTargetPower(cp, clamped);
    } catch (e: any) {
      // Common failure: the trainer disconnected mid-write. Surface
      // via the error state so the UI can nudge the rider to
      // re-pair. Don't clear the number itself.
      setError(e?.message || 'Trainer rejected the target-power write');
    }
  }, []);

  // ---- Heart rate ----

  const connectHr = useCallback(async () => {
    if (!navigator.bluetooth) {
      setError('Web Bluetooth is not supported in this browser. Use Chrome.');
      return;
    }
    setError(null);
    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [HEART_RATE_SERVICE] }],
      });
      setHrDevice(device.name ?? 'HR monitor');

      device.addEventListener('gattserverdisconnected', () => {
        setHrConnected(false);
        setHrDevice(null);
        hrGattRef.current = null;
      });

      const server = await device.gatt!.connect();
      hrGattRef.current = server;
      setHrConnected(true);

      const service = await server.getPrimaryService(HEART_RATE_SERVICE);
      const char = await service.getCharacteristic(HEART_RATE_MEASUREMENT);

      char.addEventListener(
        'characteristicvaluechanged',
        (event: Event) => {
          const value = (event.target as BluetoothRemoteGATTCharacteristic)
            .value!;
          const reading = parseHeartRate(value);
          hrRef.current = reading;
          setHeartRate(reading);
        }
      );

      await char.startNotifications();
    } catch (e: any) {
      if (e.name === 'NotFoundError') return;
      setError(e.message || 'Failed to connect HR monitor');
    }
  }, []);

  const disconnectHr = useCallback(() => {
    if (hrGattRef.current?.connected) {
      hrGattRef.current.disconnect();
    }
    hrGattRef.current = null;
    setHrConnected(false);
    setHrDevice(null);
    setHeartRate(null);
    hrRef.current = null;
  }, []);

  // ---- Combined sensor data getter (called by the vision pipeline) ----

  const getSensorData = useCallback(() => {
    const p = powerRef.current;
    const hr = hrRef.current;
    if (!p && !hr) return undefined;

    return {
      power_w: p?.watts,
      cadence_rpm: p?.cadenceRpm ?? undefined,
      heart_rate_bpm: hr?.bpm,
    };
  }, []);

  // ---- Parsers ----

  function parseCyclingPower(data: DataView): PowerReading {
    // Cycling Power Measurement characteristic (0x2A63)
    // Flags (16 bit) | Instantaneous Power (sint16) | ...optional fields
    const flags = data.getUint16(0, true);
    const watts = data.getInt16(2, true);

    let cadenceRpm: number | null = null;

    // Bit 5: Crank Revolution Data Present
    if (flags & (1 << 5)) {
      // Find the offset — skip optional fields before crank data.
      // The spec defines a fixed order; we need to count which optional
      // fields are present before crank data.
      let offset = 4;
      if (flags & (1 << 0)) offset += 1; // Pedal Power Balance
      if (flags & (1 << 2)) offset += 2; // Accumulated Torque
      if (flags & (1 << 4)) {
        offset += 4; // Wheel Revolution Data (cumRev + lastTime)
      }

      // Crank Revolution Data: cumulative revolutions (uint16) +
      // last crank event time (uint16, in 1/1024 sec)
      if (offset + 4 <= data.byteLength) {
        const cumCrankRevs = data.getUint16(offset, true);
        const lastCrankEventTime = data.getUint16(offset + 2, true);

        if (
          lastCrankRevs.current !== null &&
          lastCrankTime.current !== null
        ) {
          let dRevs = cumCrankRevs - lastCrankRevs.current;
          let dTime = lastCrankEventTime - lastCrankTime.current;
          // Handle uint16 overflow
          if (dRevs < 0) dRevs += 65536;
          if (dTime < 0) dTime += 65536;
          if (dTime > 0) {
            // dTime is in units of 1/1024 seconds
            cadenceRpm = Math.round((dRevs / (dTime / 1024)) * 60);
            // Sanity-check: cap at 200 RPM
            if (cadenceRpm > 200) cadenceRpm = null;
          }
        }
        lastCrankRevs.current = cumCrankRevs;
        lastCrankTime.current = lastCrankEventTime;
      }
    }

    return { watts, cadenceRpm, timestamp: Date.now() };
  }

  function parseHeartRate(data: DataView): HeartRateReading {
    // Heart Rate Measurement characteristic (0x2A37)
    // Flags (8 bit) | HR Value (uint8 or uint16) | ...
    const flags = data.getUint8(0);
    const is16Bit = flags & 0x01;
    const bpm = is16Bit ? data.getUint16(1, true) : data.getUint8(1);
    return { bpm, timestamp: Date.now() };
  }

  return {
    powerDevice,
    powerConnected,
    power,
    connectPower,
    disconnectPower,
    hrDevice,
    hrConnected,
    heartRate,
    connectHr,
    disconnectHr,
    hasErgControl,
    targetPowerW,
    setTargetPower,
    getSensorData,
    error,
  };
}

// Wave 20.44 — FTMS Set Target Power (op 0x05) writes a signed
// int16 little-endian watts value after the op byte.
async function writeSetTargetPower(
  cp: BluetoothRemoteGATTCharacteristic,
  watts: number,
): Promise<void> {
  const buf = new ArrayBuffer(3);
  const v = new DataView(buf);
  v.setUint8(0, FTMS_OP_SET_TARGET_POWER);
  v.setInt16(1, watts, /* littleEndian */ true);
  await cp.writeValueWithResponse(buf);
}
